import React from 'react';
import { Formik } from 'formik';

import { useTranslation } from 'react-i18next';
import { ChargingPolicy } from 'subdomains/cpo/types/charging-policy';
import {
  Button,
  Container,
  Form,
  Header,
  Message,
  Divider,
  FormGroup,
} from 'semantic';
import { chargingPolicySchema } from '../../validation';
import { FeatureFlag } from 'components';
import InputField from 'components/form-fields/formik/InputField';
import DropdownField from 'components/form-fields/formik/DropdownField';
import { useUser } from 'contexts/user';
import { TariffRules } from './TariffRules';
import PriceField from 'components/form-fields/formik/PriceField';
import ChargingPolicyAssociationSelection from 'subdomains/cpo/screens/PoliciesAndGroups/ChargingPolicies/form/ChargingPolicyAssociationSelection';
import { request } from 'utils/api';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SegmentLoader } from 'subdomains/cpo/components/SegmentLoader';

type Props = {
  mode: 'create' | 'edit';
};

const defaultInitialValues: ChargingPolicy = {
  id: '',
  label: '',
  description: '',
  currency: 'EUR',
  sessionThresholds: {
    minTimeMinutes: undefined,
    minKwhConsumed: undefined,
    maxCost: undefined,
  },
  tariffRules: [],
  associatedGroups: [],
};

export const ChargingPolicyForm = ({ mode = 'create' }: Props) => {
  const { t } = useTranslation();
  const [error, setError] = React.useState<string | null>(null);
  const historyStack = useHistory();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: fetchedData, isLoading } = useQuery({
    queryKey: ['charging-policy', id],
    queryFn: async () => {
      const { data } = await request({
        method: 'GET',
        path: `/1/charging-policies/${id}`,
      });
      return data;
    },
    enabled: mode === 'edit' && !!id, // only fetch if in edit mode
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    networkMode: 'always',
  });

  const { provider } = useUser();

  const mutation = useMutation({
    mutationFn: (values: ChargingPolicy) => {
      if (mode === 'edit') {
        return request({
          method: 'PUT',
          path: `/1/charging-policies/${values?.id}`,
          body: values,
        });
      }
      return request({
        method: 'POST',
        path: '/1/charging-policies',
        body: values,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-policies'] });
      if (mode === 'create') {
        historyStack.push(
          `/charging-stations/policies-and-groups/charging-policies`
        );
      }
    },
    onError: () => {
      setError(
        t(
          'chargingPolicy.errorSubmittingForm',
          'Something went wrong while submitting the form. Please try again, or contact us if the error persists.'
        )
      );
    },
  });

  const submit = async (values: ChargingPolicy) => {
    setError(null);
    mutation.mutate(values);
  };

  if (isLoading && mode === 'edit') {
    return <SegmentLoader height={200} />;
  }

  const initialValues = fetchedData || defaultInitialValues;

  return (
    <FeatureFlag feature="policies_and_groups">
      <Container>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={chargingPolicySchema}
          onSubmit={submit}>
          {({ values, status, handleSubmit, handleReset, setFieldValue }) => (
            <Form>
              <Header
                as="h1"
                content={
                  mode === 'create'
                    ? t(
                        'chargingPolicyForm.title.create',
                        'Create Charging Policy'
                      )
                    : t('chargingPolicyForm.title.edit', 'Edit Charging Policy')
                }
              />

              {status && <Message error content={status} />}

              <Header
                as="h3"
                content={t(
                  'chargingPolicyForm.generalInfoTitle',
                  'General Info'
                )}
              />
              <InputField
                name="label"
                label={t('chargingPolicyForm.label', 'Name')}
                required
              />

              <InputField
                name="description"
                label={t('chargingPolicyForm.description', 'Description')}
              />

              <DropdownField
                name="currency"
                selection
                required
                label={t('chargingPolicyForm.currency', 'Currency')}
                options={
                  provider?.billingCurrencies.map((currency) => ({
                    key: currency,
                    value: currency,
                    text: currency,
                  })) || []
                }
              />

              <Divider hidden />

              <Header
                as="h3"
                content={t('chargingPolicyForm.pricingTitle', 'Pricing')}
              />
              <Message
                info
                content={t(
                  'chargingPolicyForm.pricingDescription',
                  'Tariffs are evaluated based on descending order (from top to bottom). Make sure that more specific tariffs (e.g. with specific payment options or customer groups) are first in the list and general tariffs (e.g. all payment options, applies to everyone) last in the list.'
                )}
              />
              <TariffRules values={values?.tariffRules} />

              <Divider hidden />

              <Header
                as="h3"
                content={t(
                  'chargingPolicyForm.sessionLimitsTitle',
                  'Session limits'
                )}
              />
              <FormGroup style={{ marginLeft: '0' }}>
                <PriceField
                  name="sessionThresholds.maxCost"
                  currency={values?.currency}
                  label={t('sessionThresholds.maxCost', 'Max session cost')}
                />
              </FormGroup>

              <Divider hidden />

              <Header
                as="h3"
                content={t(
                  'chargingPolicyForm.sessionGracePeriodTitle',
                  'Grace period'
                )}
              />
              <FormGroup>
                <InputField
                  name="sessionThresholds.minTimeMinutes"
                  label={t('sessionThresholds.minTime', 'Min session time')}
                />
                <InputField
                  name="sessionThresholds.minKwhConsumed"
                  label={t(
                    'sessionThresholds.minEnergy',
                    'Min energy consumed'
                  )}
                />
              </FormGroup>

              <Divider hidden />

              <Header
                as="h3"
                content={t(
                  'chargingPolicyForm.policyAssociationTitle',
                  'Policy association'
                )}
              />
              <ChargingPolicyAssociationSelection
                values={values?.associatedGroups}
                onChange={(values) => setFieldValue('associatedGroups', values)}
              />

              <Divider />

              {error && <Message error>{error}</Message>}

              <div style={{ float: 'right' }}>
                <Button
                  content={t('chargingPolicyForm.cancel', 'Discard')}
                  as="button"
                  basic
                  style={{ marginLeft: '0' }}
                  onClick={handleReset}
                />
                <Button
                  content={
                    mode === 'create'
                      ? t('chargingPolicyForm.createPolicy', 'Create Policy')
                      : t('chargingPolicyForm.updatePolicy', 'Update Policy')
                  }
                  as="button"
                  style={{ marginLeft: '0' }}
                  onClick={handleSubmit}
                  loading={mutation.isLoading}
                />
              </div>
            </Form>
          )}
        </Formik>
      </Container>
    </FeatureFlag>
  );
};
